/** @format */

import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
    Box,
    Typography,
    Chip,
    CircularProgress,
    Tooltip,
    Button,
} from "@mui/material";
import {
    Bolt,
    OpenInNew,
    CheckCircleOutline,
    PanTool,
    AssignmentLate,
    HeartBroken,
    Gavel,
    FactCheck,
    ChatBubbleOutline,
    ReportProblem,
    TaskAlt,
} from "@mui/icons-material";

import { CollectSectionCard } from "../styles";
import { colors } from "../../../utility/types/constant";
import { IActionCenter, IActionCenterItem } from "../../../utility/types/collectTypes";
import { submitGetActionCenter } from "../../../redux/services/collectService";
import { claimTaskApi, completeTaskApi } from "../../../redux/services/collectTaskService";

const FONT = "'Montserrat', sans-serif";

interface Props {
    businessId: string | null;
    refreshKey?: number;
}

const PRIORITY_STYLE: Record<string, { color: string; label: string }> = {
    urgent: { color: "#b91c1c", label: "Urgent" },
    high: { color: "#c2410c", label: "High" },
    medium: { color: "#1d4ed8", label: "Medium" },
    low: { color: "#6b7280", label: "Low" },
};

const TYPE_LABEL: Record<string, string> = {
    manual_review: "Manual review",
    broken_promise_followup: "Broken promise",
    review_dispute: "Dispute",
    review_complaint: "Complaint",
    verify_payment_claim: "Verify payment",
    legal_action_review: "Legal action",
    review_reply: "Reply review",
    custom: "Task",
};

function typeIcon(type: string): React.ReactNode {
    const map: Record<string, React.ReactNode> = {
        manual_review: <AssignmentLate fontSize="small" />,
        broken_promise_followup: <HeartBroken fontSize="small" />,
        review_dispute: <ReportProblem fontSize="small" />,
        review_complaint: <ChatBubbleOutline fontSize="small" />,
        verify_payment_claim: <FactCheck fontSize="small" />,
        legal_action_review: <Gavel fontSize="small" />,
        review_reply: <ChatBubbleOutline fontSize="small" />,
        custom: <TaskAlt fontSize="small" />,
    };
    return map[type] || <TaskAlt fontSize="small" />;
}

function formatMoney(amount?: number, currency?: string): string {
    if (typeof amount !== "number") return "";
    try {
        return new Intl.NumberFormat("en-US", {
            style: "currency",
            currency: currency || "USD",
            maximumFractionDigits: 0,
        }).format(amount);
    } catch {
        return `${currency || "$"}${Math.round(amount)}`;
    }
}

const ActionCenterPanel: React.FC<Props> = ({ businessId, refreshKey }) => {
    const navigate = useNavigate();
    const [data, setData] = useState<IActionCenter | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [busyTask, setBusyTask] = useState<string | null>(null);
    const requestSequence = useRef(0);
    const activeBusinessId = useRef(businessId);
    activeBusinessId.current = businessId;

    const load = useCallback(async () => {
        const requestId = ++requestSequence.current;
        if (!businessId) {
            setData(null);
            setLoading(false);
            return;
        }
        setLoading(true);
        setError("");
        setData(null);
        try {
            const res = await submitGetActionCenter(businessId, 8);
            if (requestId === requestSequence.current) setData(res);
        } catch (e: any) {
            if (requestId === requestSequence.current) {
                setError(e?.message || "Failed to load the action center.");
            }
        } finally {
            if (requestId === requestSequence.current) setLoading(false);
        }
    }, [businessId]);

    useEffect(() => {
        setBusyTask(null);
        load();
        return () => {
            requestSequence.current += 1;
        };
    }, [load, refreshKey]);

    const onClaim = async (item: IActionCenterItem) => {
        if (!businessId || busyTask) return;
        const targetBusinessId = businessId;
        setBusyTask(item.taskId);
        try {
            await claimTaskApi(targetBusinessId, item.taskId);
            if (activeBusinessId.current !== targetBusinessId) return;
            await load();
        } catch (e: any) {
            if (activeBusinessId.current === targetBusinessId) {
                setError(e?.message || "Failed to claim.");
            }
        } finally {
            if (activeBusinessId.current === targetBusinessId) setBusyTask(null);
        }
    };

    const onComplete = async (item: IActionCenterItem) => {
        if (!businessId || busyTask) return;
        const targetBusinessId = businessId;
        setBusyTask(item.taskId);
        try {
            await completeTaskApi(targetBusinessId, item.taskId);
            if (activeBusinessId.current !== targetBusinessId) return;
            await load();
        } catch (e: any) {
            if (activeBusinessId.current === targetBusinessId) {
                setError(e?.message || "Failed to complete.");
            }
        } finally {
            if (activeBusinessId.current === targetBusinessId) setBusyTask(null);
        }
    };

    const openItem = (item: IActionCenterItem) => {
        if (item.invoiceId) navigate(`/collect/invoices/${item.invoiceId}`);
        else navigate("/collect/tasks");
    };

    if (!businessId) return null;

    const summary = data?.summary;
    const items = data?.items ?? [];

    return (
        <CollectSectionCard>
            {/* Header */}
            <Box
                sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    flexWrap: "wrap",
                    gap: 1,
                    mb: 0.5,
                }}
            >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Bolt sx={{ color: colors.primary }} />
                    <Typography
                        sx={{ fontFamily: FONT, fontWeight: 800, fontSize: "1.05rem", color: "#111827" }}
                    >
                        Action Center
                    </Typography>
                    {summary && summary.total > 0 && (
                        <Chip
                            label={`${summary.total} open`}
                            size="small"
                            sx={{
                                backgroundColor: "#eef2ff",
                                color: "#3730a3",
                                fontFamily: FONT,
                                fontWeight: 700,
                                fontSize: "0.68rem",
                            }}
                        />
                    )}
                </Box>
                <Typography
                    onClick={() => navigate("/collect/tasks")}
                    sx={{
                        fontFamily: FONT,
                        fontSize: "0.76rem",
                        fontWeight: 700,
                        color: colors.primary,
                        cursor: "pointer",
                        "&:hover": { textDecoration: "underline" },
                    }}
                >
                    View all
                </Typography>
            </Box>
            <Typography
                sx={{ fontFamily: FONT, fontSize: "0.78rem", color: "#6b7280", mb: 1.5 }}
            >
                Items the platform paused for you with the recommended next step.
            </Typography>

            {/* Priority summary chips */}
            {summary && summary.total > 0 && (
                <Box sx={{ display: "flex", gap: 0.75, flexWrap: "wrap", mb: 1.5 }}>
                    {(["urgent", "high", "medium", "low"] as const)
                        .filter((p) => (summary[p] || 0) > 0)
                        .map((p) => (
                            <Chip
                                key={p}
                                label={`${PRIORITY_STYLE[p].label}: ${summary[p]}`}
                                size="small"
                                sx={{
                                    fontFamily: FONT,
                                    fontSize: "0.68rem",
                                    fontWeight: 700,
                                    backgroundColor: `${PRIORITY_STYLE[p].color}14`,
                                    color: PRIORITY_STYLE[p].color,
                                }}
                            />
                        ))}
                </Box>
            )}

            {error && (
                <Typography
                    sx={{ fontFamily: FONT, fontSize: "0.78rem", color: "#b91c1c", mb: 1 }}
                >
                    {error}
                </Typography>
            )}

            {loading && !data ? (
                <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                    <CircularProgress size={24} sx={{ color: colors.primary }} />
                </Box>
            ) : items.length === 0 ? (
                <Box sx={{ textAlign: "center", py: 4 }}>
                    <CheckCircleOutline sx={{ fontSize: "2rem", color: "#86efac", mb: 1 }} />
                    <Typography
                        sx={{ fontFamily: FONT, fontSize: "0.9rem", color: "#374151", fontWeight: 700 }}
                    >
                        Nothing needs you right now
                    </Typography>
                    <Typography
                        sx={{ fontFamily: FONT, fontSize: "0.76rem", color: "#9ca3af" }}
                    >
                        The platform is handling everything automatically.
                    </Typography>
                </Box>
            ) : (
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                    {items.map((item) => {
                        const ps = PRIORITY_STYLE[item.priority] || PRIORITY_STYLE.low;
                        const isBusy = busyTask === item.taskId;
                        return (
                            <Box
                                key={item.taskId}
                                sx={{
                                    border: "1px solid #e5e7eb",
                                    borderLeft: `3px solid ${ps.color}`,
                                    borderRadius: "10px",
                                    p: 1.5,
                                    display: "flex",
                                    flexDirection: { xs: "column", md: "row" },
                                    gap: 1.5,
                                    alignItems: { xs: "stretch", md: "center" },
                                    backgroundColor: "#fff",
                                    transition: "box-shadow 0.15s ease",
                                    "&:hover": { boxShadow: "0 4px 14px rgba(0,0,0,0.06)" },
                                }}
                            >
                                {/* Left: context */}
                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, flexWrap: "wrap" }}>
                                        <Box sx={{ color: ps.color, display: "inline-flex" }}>
                                            {typeIcon(item.type)}
                                        </Box>
                                        <Typography
                                            sx={{ fontFamily: FONT, fontWeight: 700, fontSize: "0.86rem", color: "#111827" }}
                                        >
                                            {item.customerDisplayName || "Customer"}
                                        </Typography>
                                        {item.docNumber && (
                                            <Typography
                                                sx={{ fontFamily: FONT, fontSize: "0.74rem", color: "#6b7280" }}
                                            >
                                                · Invoice #{item.docNumber}
                                            </Typography>
                                        )}
                                        <Chip
                                            label={TYPE_LABEL[item.type] || "Task"}
                                            size="small"
                                            sx={{
                                                height: 18,
                                                fontFamily: FONT,
                                                fontSize: "0.62rem",
                                                fontWeight: 700,
                                                backgroundColor: `${ps.color}14`,
                                                color: ps.color,
                                            }}
                                        />
                                    </Box>

                                    {/* Money + age line */}
                                    <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, mt: 0.5, flexWrap: "wrap" }}>
                                        {typeof item.balance === "number" && (
                                            <Typography sx={{ fontFamily: FONT, fontSize: "0.78rem", color: "#111827", fontWeight: 700 }}>
                                                {formatMoney(item.balance, item.currency)}
                                            </Typography>
                                        )}
                                        {typeof item.daysOverdue === "number" && item.daysOverdue > 0 && (
                                            <Typography sx={{ fontFamily: FONT, fontSize: "0.74rem", color: "#b45309", fontWeight: 600 }}>
                                                {item.daysOverdue}d overdue
                                            </Typography>
                                        )}
                                        {item.brokenPromiseCount ? (
                                            <Typography sx={{ fontFamily: FONT, fontSize: "0.74rem", color: "#b91c1c", fontWeight: 600 }}>
                                                {item.brokenPromiseCount} broken promise{item.brokenPromiseCount === 1 ? "" : "s"}
                                            </Typography>
                                        ) : null}
                                        <Typography sx={{ fontFamily: FONT, fontSize: "0.72rem", color: "#9ca3af" }}>
                                            waiting {item.ageDays}d
                                        </Typography>
                                    </Box>

                                    {/* Recommended action */}
                                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mt: 0.5 }}>
                                        <Bolt sx={{ fontSize: "0.9rem", color: colors.primary }} />
                                        <Typography sx={{ fontFamily: FONT, fontSize: "0.76rem", color: "#374151" }}>
                                            <strong>{item.recommendedAction.label}</strong>
                                            {item.recommendedAction.hint ? ` — ${item.recommendedAction.hint}` : ""}
                                        </Typography>
                                    </Box>
                                </Box>

                                {/* Right: actions */}
                                <Box sx={{ display: "flex", gap: 0.75, flexShrink: 0, alignItems: "center" }}>
                                    <Button
                                        size="small"
                                        variant="contained"
                                        startIcon={<OpenInNew sx={{ fontSize: "1rem !important" }} />}
                                        onClick={() => openItem(item)}
                                        sx={{
                                            textTransform: "none",
                                            fontFamily: FONT,
                                            fontWeight: 700,
                                            fontSize: "0.74rem",
                                            backgroundColor: colors.primary,
                                            boxShadow: "none",
                                            "&:hover": { boxShadow: "none", opacity: 0.92 },
                                        }}
                                    >
                                        Open
                                    </Button>
                                    {!item.assigned && (
                                        <Tooltip title="Assign to me" arrow>
                                            <span>
                                                <Button
                                                    size="small"
                                                    variant="outlined"
                                                    disabled={isBusy}
                                                    startIcon={<PanTool sx={{ fontSize: "0.9rem !important" }} />}
                                                    onClick={() => onClaim(item)}
                                                    sx={{
                                                        textTransform: "none",
                                                        fontFamily: FONT,
                                                        fontWeight: 700,
                                                        fontSize: "0.74rem",
                                                        color: "#374151",
                                                        borderColor: "#d1d5db",
                                                    }}
                                                >
                                                    Claim
                                                </Button>
                                            </span>
                                        </Tooltip>
                                    )}
                                    <Tooltip title="Mark this action done" arrow>
                                        <span>
                                            <Button
                                                size="small"
                                                variant="outlined"
                                                disabled={isBusy}
                                                startIcon={
                                                    isBusy ? (
                                                        <CircularProgress size={12} sx={{ color: "#15803d" }} />
                                                    ) : (
                                                        <CheckCircleOutline sx={{ fontSize: "1rem !important" }} />
                                                    )
                                                }
                                                onClick={() => onComplete(item)}
                                                sx={{
                                                    textTransform: "none",
                                                    fontFamily: FONT,
                                                    fontWeight: 700,
                                                    fontSize: "0.74rem",
                                                    color: "#15803d",
                                                    borderColor: "#bbf7d0",
                                                }}
                                            >
                                                Done
                                            </Button>
                                        </span>
                                    </Tooltip>
                                </Box>
                            </Box>
                        );
                    })}
                </Box>
            )}
        </CollectSectionCard>
    );
};

export default ActionCenterPanel;
